import React, { useMemo } from "react";
import {
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  DialogActions,
  IconButton,
  Paper,
  Button,
  TextField,
  TableRow,
  TableHead,
  Table,
  TableContainer,
  TableBody,
  Autocomplete,
  MenuItem,
  Checkbox,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import SaveTwoToneIcon from "@mui/icons-material/SaveTwoTone";
import DeleteIcon from "@mui/icons-material/DeleteForeverTwoTone";
import {
  baseFontStyle,
  menuItemTextStyle,
  StyledTableCell,
} from "../../tickets/tickets";
import { ROLE_OPTIONS } from "../constants/ticketConstants";

const BauTeamMembers = (props) => {
  const {
    bauTeamDialog,
    internalTeam,
    bauTeamRows,
    editingRows,
    setEditingRows,
    handleBauTeamDialogClose,
    handleAddBauMember,
    handleBauMemberChange,
    handleActiveChange,
    handleRowSave,
    handleSaveBauMember,
    handleDeleteBauMember,
    disableSave,
  } = props;

  const disable = useMemo(() => {
    if (editingRows[0]) {
      return !!bauTeamRows?.find(
        (newMember) =>
          newMember?.isNew &&
          (newMember?.name === "" ||
            newMember?.email === "" ||
            newMember?.role === ""),
      );
    }
    return false;
  }, [editingRows, bauTeamRows]);

  const saveDisable = useMemo(() => {
    if (editingRows[0]) {
      return !!bauTeamRows?.find(
        (newMember) => newMember?.isNew && newMember?.isSaved,
      );
    } else {
      return disableSave;
    }
  }, [editingRows, bauTeamRows, disableSave]);
  return (
    <Dialog
      open={bauTeamDialog.open}
      onClose={handleBauTeamDialogClose}
      maxWidth="lg"
      fullWidth
    >
      <DialogTitle
        sx={{
          ...baseFontStyle,
          fontSize: "1rem",
          fontWeight: 600,
        }}
      >
        BAU Team Details
      </DialogTitle>

      <DialogContent>
        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            mb: 2,
          }}
        >
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            sx={{
              ...menuItemTextStyle,
              textTransform: "none",
            }}
            onClick={handleAddBauMember}
          >
            Add BAU Member
          </Button>
        </Box>
        <TableContainer
          component={Paper}
          sx={{
            mt: 2,
            maxHeight: 400,
          }}
        >
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                {["Name", "Role", "Email", "Active", "Actions"].map(
                  (header, index) => (
                    <StyledTableCell key={header + index}>
                      {header}
                    </StyledTableCell>
                  ),
                )}
              </TableRow>
            </TableHead>

            <TableBody>
              {bauTeamRows.map((member, index) => {
                const isEditing = member?.isNew;

                return (
                  <TableRow key={index}>
                    <StyledTableCell>
                      {isEditing ? (
                        <Autocomplete
                          size="small"
                          options={internalTeam}
                          getOptionLabel={(option) => option?.name || ""}
                          value={
                            internalTeam.find((x) => x.name === member.name) ||
                            null
                          }
                          onChange={(_, newValue) => {
                            handleBauMemberChange(
                              index,
                              "name",
                              newValue?.name || "",
                            );

                            handleBauMemberChange(
                              index,
                              "email",
                              newValue?.email || "",
                            );
                            handleBauMemberChange(
                              index,
                              "user_id",
                              newValue?.id || null,
                            );
                          }}
                          renderInput={(params) => (
                            <TextField {...params} placeholder="Select Name" />
                          )}
                        />
                      ) : (
                        member.name
                      )}
                    </StyledTableCell>

                    <StyledTableCell>
                      {isEditing ? (
                        <TextField
                          select
                          size="small"
                          fullWidth
                          value={member.role || ""}
                          onChange={(e) => {
                            handleBauMemberChange(
                              index,
                              "role",
                              e.target.value,
                            );
                            handleBauMemberChange(index, "isSaved", true);
                          }}
                        >
                          {ROLE_OPTIONS.map((role) => (
                            <MenuItem key={role} value={role}>
                              {role}
                            </MenuItem>
                          ))}
                        </TextField>
                      ) : (
                        member.role
                      )}
                    </StyledTableCell>

                    <StyledTableCell>
                      {isEditing ? (
                        <Autocomplete
                          size="small"
                          options={internalTeam}
                          getOptionLabel={(option) => option?.email || ""}
                          value={
                            internalTeam.find(
                              (x) => x.email === member.email,
                            ) || null
                          }
                          onChange={(_, newValue) => {
                            handleBauMemberChange(
                              index,
                              "email",
                              newValue?.email || "",
                            );

                            handleBauMemberChange(
                              index,
                              "name",
                              newValue?.name || "",
                            );
                            handleBauMemberChange(
                              index,
                              "user_id",
                              newValue?.id || null,
                            );
                          }}
                          renderInput={(params) => (
                            <TextField {...params} placeholder="Select Email" />
                          )}
                        />
                      ) : (
                        member.email
                      )}
                    </StyledTableCell>

                    <StyledTableCell>
                      <Checkbox
                        checked={member.active ?? true}
                        onChange={(e) =>
                          handleActiveChange(index, e.target.checked)
                        }
                      />
                    </StyledTableCell>

                    <StyledTableCell>
                      {isEditing && (
                        <IconButton
                          size="small"
                          disabled={disable}
                          onClick={() => {
                            handleBauMemberChange(index, "isNew", false);
                            handleRowSave(index);
                          }}
                        >
                          <SaveTwoToneIcon />
                        </IconButton>
                      )}

                      <IconButton
                        color="error"
                        size="small"
                        onClick={() => handleDeleteBauMember(index)}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </StyledTableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </DialogContent>

      <DialogActions>
        <Button
          onClick={handleBauTeamDialogClose}
          sx={{
            ...menuItemTextStyle,
            textTransform: "none",
          }}
        >
          Close
        </Button>
        <Button
          variant="contained"
          disabled={saveDisable}
          sx={{
            ...menuItemTextStyle,
            textTransform: "none",
          }}
          onClick={handleSaveBauMember}
        >
          Save Changes
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default BauTeamMembers;
